import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { RoleProvider, useRole } from "@/contexts/RoleContext";
import { GradeProvider } from "@/contexts/GradeContext";
import { RequireStudentAuth, RequireTeacherAuth } from "@/components/AuthGuard";
import AppHeader from "@/components/AppHeader";
import StudentLogin from "@/pages/StudentLogin";
import TeacherLogin from "@/pages/TeacherLogin";
import WelcomeHome from "@/pages/WelcomeHome";
import WelcomeScreen from "@/pages/WelcomeScreen";
import TeacherWelcomeScreen from "@/pages/TeacherWelcomeScreen";
import SubjectOverview from "@/pages/SubjectOverview";
import SessionRunner from "@/pages/SessionRunner";
import JourneyMap from "@/pages/JourneyMap";
import TeacherDashboard from "@/pages/TeacherDashboard";
import TeacherClassCockpit from "@/pages/TeacherClassCockpit";
import TeacherClassPrint from "@/pages/TeacherClassPrint";
import TeacherStudent from "@/pages/TeacherStudent";
import TeacherStudentPrint from "@/pages/TeacherStudentPrint";
import TeacherStopAlert from "@/pages/TeacherStopAlert";
import TeacherSubtopicDrill from "@/pages/TeacherSubtopicDrill";
import TeacherHeatmap from "@/pages/TeacherHeatmap";
import AdminApp from "@/pages/admin/AdminApp";
import MitraaWelcome from "@/pages/MitraaWelcome";
import MathFoundations from "@/pages/MathFoundations";
import FMSessionRunner from "@/pages/FMSessionRunner";
import TeacherFMPanel from "@/pages/TeacherFMPanel";
import FLNModule from "@/pages/FLNModule";
import StudentTests from "@/pages/StudentTests";
import StudentTestTake from "@/pages/StudentTestTake";
import TryMitraa from "@/pages/TryMitraa";

// Routes where AppHeader should be suppressed (full-screen immersive/interstitials)
const NO_HEADER_ROUTES = new Set(["/welcome-screen", "/teacher/welcome"]);

function Shell() {
  const loc = useLocation();
  const isTeacherRoute = loc.pathname.startsWith("/teacher") && loc.pathname !== "/teacher/login";
  const isStudentRoute =
    !loc.pathname.startsWith("/teacher") &&
    !loc.pathname.startsWith("/admin") &&
    loc.pathname !== "/login" &&
    loc.pathname !== "/try" &&
    loc.pathname !== "/";

  const showHeader = (isStudentRoute || isTeacherRoute) && !NO_HEADER_ROUTES.has(loc.pathname);

  return (
    <div className={isTeacherRoute ? "teacher-scope min-h-screen" : "min-h-screen"}>
      {showHeader && <AppHeader />}
      
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<MitraaWelcome />} />
        <Route path="/login" element={<StudentLogin />} />
        <Route path="/teacher/login" element={<TeacherLogin />} />
        <Route path="/try" element={<TryMitraa />} />

        {/* Welcome screen interstitials — post-login */}
        <Route path="/welcome-screen" element={<RequireStudentAuth><WelcomeScreen /></RequireStudentAuth>} />
        <Route path="/teacher/welcome" element={<RequireTeacherAuth><TeacherWelcomeScreen /></RequireTeacherAuth>} />

        {/* Protected student routes */}
        <Route path="/home" element={<RequireStudentAuth><WelcomeHome /></RequireStudentAuth>} />
        <Route path="/subject/:subject" element={<RequireStudentAuth><SubjectOverview /></RequireStudentAuth>} />
        <Route path="/session/:sessionId" element={<RequireStudentAuth><SessionRunner /></RequireStudentAuth>} />
        <Route path="/journey" element={<RequireStudentAuth><JourneyMap /></RequireStudentAuth>} />
        <Route path="/math-foundations" element={<RequireStudentAuth><MathFoundations /></RequireStudentAuth>} />
        <Route path="/fln" element={<RequireStudentAuth><FLNModule /></RequireStudentAuth>} />
        <Route path="/fm/:skill" element={<RequireStudentAuth><FMSessionRunner /></RequireStudentAuth>} />
        <Route path="/tests" element={<RequireStudentAuth><StudentTests /></RequireStudentAuth>} />
        <Route path="/tests/:testId" element={<RequireStudentAuth><StudentTestTake /></RequireStudentAuth>} />

        {/* Protected teacher routes */}
        <Route path="/teacher" element={<RequireTeacherAuth><TeacherDashboard /></RequireTeacherAuth>} />
        <Route path="/teacher/cockpit/:grade" element={<RequireTeacherAuth><TeacherDashboard /></RequireTeacherAuth>} />
        <Route path="/teacher/cockpit/:grade/:subject" element={<RequireTeacherAuth><TeacherClassCockpit /></RequireTeacherAuth>} />
        <Route path="/teacher/print/:grade/:subject" element={<RequireTeacherAuth><TeacherClassPrint /></RequireTeacherAuth>} />
        <Route path="/teacher/heatmap" element={<RequireTeacherAuth><TeacherHeatmap /></RequireTeacherAuth>} />
        <Route path="/teacher/alert/:stopId" element={<RequireTeacherAuth><TeacherStopAlert /></RequireTeacherAuth>} />
        <Route path="/teacher/student/:learnerId" element={<RequireTeacherAuth><TeacherStudent /></RequireTeacherAuth>} />
        <Route path="/teacher/student/:learnerId/print" element={<RequireTeacherAuth><TeacherStudentPrint /></RequireTeacherAuth>} />
        <Route path="/teacher/student/:learnerId/subtopic/:subject/:subTopic" element={<RequireTeacherAuth><TeacherSubtopicDrill /></RequireTeacherAuth>} />
        <Route path="/teacher/fm-panel/:grade" element={<RequireTeacherAuth><TeacherFMPanel /></RequireTeacherAuth>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <Toaster />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Admin panel — completely separate, no AppHeader, no role context */}
        <Route path="/admin/*" element={<AdminApp />} />
        {/* Main learner/teacher app */}
        <Route
          path="/*"
          element={
            <RoleProvider>
              <GradeProvider>
                <Shell />
              </GradeProvider>
            </RoleProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
